import { AxiosInstance } from 'axios';
import CreateHttpClient from './createTimetablesWebApiHttpClient';

import { OfficeHour, OfficeHourBooking, OfficeHourSettings } from './../models/office-hour';
import { openConfirmModal } from '../helpers/openConfirmModal';

class OfficeHoursApi {
    private httpClient: AxiosInstance;

    constructor() {
        this.httpClient = CreateHttpClient();
    }

    async GetSettings(): Promise<OfficeHourSettings> {
        const { data } = await this.httpClient.get(`/officeHour/settings`);
        return data;
    }

    async UpdateSettings(settings: OfficeHourSettings) {
        const { data } = await this.httpClient.put(`/officeHour/settings`, settings);
        return data;
    }

    async GetOfficeHours(instructorId: number, termStartDate: string, termEndDate: string) {
        const { data } = await this.httpClient.get(`/officeHour?instructorId=${instructorId}&termStartDate=${termStartDate}&termEndDate=${termEndDate}`);
        return data;
    }

    // inside your service class
    private async confirmableRequest(method: 'post' | 'put', url: string, payload: any): Promise<boolean> {
        const first = await this.httpClient.request({
            method,
            url,
            data: payload,
            // allow confirmable response to resolve instead of throw
            validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
        });

        if (first.status >= 200 && first.status < 300) return true;

        const problem = first.data;

        if (problem?.requiresConfirmation && problem?.confirmationToken) {
            const ok = await openConfirmModal(problem.confirmableWarnings);
            if (!ok) return false;

            const second = await this.httpClient.request({
                method,
                url,
                data: payload,
                headers: { 'X-Confirmation-Token': problem.confirmationToken },
                validateStatus: (s) => s >= 200 && s < 300,
            });

            return second.status >= 200 && second.status < 300;
        }

        return false;
    }

    async AddOfficeHour(officeHour: OfficeHour) {
        return this.confirmableRequest('post', '/officeHour', officeHour);
    }

    async UpdateOfficeHour(officeHour: OfficeHour) {
        return this.confirmableRequest('put', '/officeHour', officeHour);
    }

    async DeleteOfficeHour(instructorId: number, officeHourId: number) {
        const { data } = await this.httpClient.delete(`/officeHour?instructorId=${instructorId}&id=${officeHourId}`);
        return data;
    }

    async DeleteAllOfficeHours(instructorId: number, startDate: string, endDate: string) {
        const { data } = await this.httpClient.delete(`/officeHour/bulkDelete?instructorId=${instructorId}&startDate=${startDate}&endDate=${endDate}`);
        return data;
    }

    async GetRoles() {
        const { data } = await this.httpClient.get(`/role`);
        return data;
    }

    async GetTimeSlotBooking(officeHourId: number) {
        const { data } = await this.httpClient.get(`/officeHour/timeSlot?officeHourId=${officeHourId}`);
        return data;
    }

    async AddTimeSlotBooking(booking: OfficeHourBooking) {
        const { data } = await this.httpClient.post(`/officeHour/timeSlot`, booking);
        return data;
    }

    async UpdateTimeSlotBooking(booking: OfficeHourBooking) {
        const { data } = await this.httpClient.put(`/officeHour/timeSlot`, booking);
        return data;
    }

    async DeleteTimeSlotBooking(bookingId: number) {
        const { data } = await this.httpClient.delete(`/officeHour/timeSlot?id=${bookingId}`);
        return data;
    }
}

export default OfficeHoursApi;
